import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaCoins, FaSpinner } from "react-icons/fa";
import apiRequest from "../services/api";

export default function AccountBalanceAmendModal({
  isOpen,
  onClose,
  onSuccess,
  defaultAccountCode = "INCOME_TAX_PAYABLE",
  title = "Amend Account Balance",
}) {
  const [form, setForm] = useState({
    amount: "",
    increase: true,
    reference: "",
    note: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setIsLoading(false);
    setForm({
      amount: "",
      increase: true,
      reference: "",
      note: "",
    });
  }, [isOpen]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) {
      setError("Amount must be greater than 0.00.");
      return;
    }

    const payload = {
      targetAccountCode: defaultAccountCode,
      amount,
      increase: !!form.increase,
      reference: form.reference?.trim() || null,
      note: form.note?.trim() || null,
    };

    setIsLoading(true);
    try {
      await apiRequest("reports/amend-balance", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Account amendment failed.");
    } finally {
      setIsLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={!isLoading ? onClose : undefined}
      />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh]
                   bg-slate-900 border border-white/10
                   rounded-t-2xl sm:rounded-2xl
                   shadow-2xl overflow-y-auto
                   safe-area-pb"
      >
        {/* Sticky Header */}
        <div
          className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur
                        flex items-start justify-between gap-3
                        px-4 py-3 sm:px-5 sm:py-4
                        border-b border-white/5"
        >
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-white uppercase tracking-tight flex items-center gap-2">
              <FaCoins className="text-amber-400 shrink-0" />
              <span className="truncate">{title}</span>
            </h3>
            <p className="text-[10px] sm:text-[11px] text-slate-400 mt-1 leading-relaxed">
              Account: <span className="font-mono text-amber-400/90">{defaultAccountCode}</span>
            </p>
          </div>
          <button
            type="button"
            disabled={isLoading}
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition shrink-0"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-4 py-4 sm:px-5 sm:py-5 space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Direction */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase font-bold text-slate-400">
                Direction
              </label>
              <select
                required
                disabled={isLoading}
                value={form.increase ? "increase" : "decrease"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    increase: e.target.value === "increase",
                  }))
                }
                className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-white focus:outline-none focus:border-amber-500/40"
              >
                <option value="increase">Increase Balance</option>
                <option value="decrease">Decrease Balance</option>
              </select>
            </div>

            {/* Amount */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] uppercase font-bold text-slate-400">
                Amount
              </label>
              <input
                required
                disabled={isLoading}
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) =>
                  setForm((p) => ({ ...p, amount: e.target.value }))
                }
                className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-white text-right font-mono focus:outline-none focus:border-amber-500/40"
              />
            </div>
          </div>

          {/* Note */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] uppercase font-bold text-slate-400">
              Note
            </label>
            <textarea
              disabled={isLoading}
              rows={3}
              placeholder="Reason for amendment..."
              value={form.note}
              onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
              className="w-full bg-slate-950 border border-white/10 rounded-xl px-3 sm:px-4 py-2.5 text-xs text-white resize-none focus:outline-none focus:border-amber-500/40"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-3 border-t border-white/5">
            <button
              type="button"
              disabled={isLoading}
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-white/5 text-slate-300 text-xs font-semibold hover:bg-white/10 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold flex items-center justify-center gap-2 shadow-md transition disabled:opacity-60"
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <FaCoins />
                  Post Amendment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}